import React from 'react';
import { Skeleton, SkeletonStats } from '../ui/Skeleton';
import { BookingCardSkeleton } from './BookingCardSkeleton';
import { cn } from '../../utils/cn';
import type { SkeletonAnimation } from '../ui/Skeleton/Skeleton.types';

interface DashboardSkeletonProps {
  /**
   * Variant for different dashboard types
   * @default 'user'
   */
  variant?: 'user' | 'therapist' | 'admin';

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Animation type
   * @default 'pulse'
   */
  animation?: SkeletonAnimation;
}

/**
 * Skeleton placeholder for dashboard pages
 *
 * Provides a full page skeleton that matches the layout of
 * user, therapist, and admin dashboards including:
 * - Header section
 * - Stats cards
 * - Content cards/tables
 */
export const DashboardSkeleton: React.FC<DashboardSkeletonProps> = ({
  variant = 'user',
  className,
  animation = 'pulse',
}) => {
  return (
    <div className={cn('min-h-screen bg-gray-50 dark:bg-slate-900 py-8 px-4', className)}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Skeleton className="h-8 w-48 mb-2" animation={animation} />
          <Skeleton className="h-5 w-72" animation={animation} />
        </div>

        {/* Stats Grid */}
        <SkeletonStats count={variant === 'admin' ? 4 : 3} className="mb-8" animation={animation} />

        {/* Quick Actions (for user dashboard) */}
        {variant === 'user' && (
          <div className="mb-6 flex gap-4">
            <Skeleton className="h-10 w-40" variant="rectangular" animation={animation} />
            <Skeleton className="h-10 w-36" variant="rectangular" animation={animation} />
          </div>
        )}

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-slate-700 mb-6">
          <div className="flex gap-8">
            <Skeleton className="h-12 w-28" animation={animation} />
            <Skeleton className="h-12 w-24" animation={animation} />
          </div>
        </div>

        {/* Booking Cards Grid */}
        <div className="grid gap-4 sm:grid-cols-2">
          <BookingCardSkeleton animation={animation} />
          <BookingCardSkeleton animation={animation} />
          <BookingCardSkeleton animation={animation} />
          <BookingCardSkeleton animation={animation} />
        </div>
      </div>
    </div>
  );
};

/**
 * Skeleton for admin dashboard with table layout
 */
export const AdminDashboardSkeleton: React.FC<{
  className?: string;
  animation?: SkeletonAnimation;
}> = ({ className, animation = 'pulse' }) => {
  return (
    <div className={cn('space-y-6', className)}>
      {/* Page Header */}
      <div>
        <Skeleton className="h-8 w-32 mb-2" animation={animation} />
        <Skeleton className="h-5 w-56" animation={animation} />
      </div>

      {/* Stats Grid */}
      <SkeletonStats count={4} animation={animation} />

      {/* Quick Actions Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4"
          >
            <Skeleton
              variant="rectangular"
              width={40}
              height={40}
              className="rounded-lg"
              animation={animation}
            />
            <div className="flex-1">
              <Skeleton className="h-5 w-32 mb-1" animation={animation} />
              <Skeleton className="h-4 w-24" animation={animation} />
            </div>
          </div>
        ))}
      </div>

      {/* Recent Bookings Table */}
      <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-200 dark:border-slate-700 px-6 py-4">
          <Skeleton className="h-6 w-36" animation={animation} />
          <Skeleton className="h-5 w-16" animation={animation} />
        </div>
        <div className="divide-y divide-gray-200 dark:divide-slate-700">
          {Array.from({ length: 5 }).map((_, index) => (
            <div key={index} className="flex items-center justify-between px-6 py-4">
              <div className="flex items-center gap-4">
                <Skeleton variant="circular" width={40} height={40} animation={animation} />
                <div>
                  <Skeleton className="h-5 w-32 mb-1" animation={animation} />
                  <Skeleton className="h-4 w-48" animation={animation} />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <Skeleton className="h-4 w-16 mb-1" animation={animation} />
                  <Skeleton className="h-3 w-24" animation={animation} />
                </div>
                <Skeleton className="h-6 w-20" variant="rectangular" animation={animation} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Skeleton for therapist dashboard
 */
export const TherapistDashboardSkeleton: React.FC<{
  className?: string;
  animation?: SkeletonAnimation;
}> = ({ className, animation = 'pulse' }) => {
  return (
    <div className={cn('space-y-8', className)}>
      {/* Welcome Banner */}
      <div className="bg-gray-200 dark:bg-slate-700 rounded-lg p-6 md:p-8 animate-pulse">
        <Skeleton className="h-8 w-64 mb-2 bg-gray-300 dark:bg-slate-600" animation="none" />
        <Skeleton className="h-5 w-48 bg-gray-300 dark:bg-slate-600" animation="none" />
      </div>

      {/* Stats Grid */}
      <SkeletonStats count={4} animation={animation} />

      {/* Next Appointment */}
      <div className="bg-white dark:bg-slate-800 rounded-lg p-6 border-2 border-gray-200 dark:border-slate-700">
        <div className="flex items-start justify-between">
          <div>
            <Skeleton className="h-6 w-36 mb-2" animation={animation} />
            <Skeleton className="h-8 w-20 mb-1" animation={animation} />
            <Skeleton className="h-5 w-32 mb-1" animation={animation} />
            <Skeleton className="h-4 w-28" animation={animation} />
          </div>
          <Skeleton className="h-12 w-28" variant="rectangular" animation={animation} />
        </div>
      </div>

      {/* Today's Schedule Section */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <Skeleton className="h-8 w-44" animation={animation} />
          <Skeleton className="h-5 w-32" animation={animation} />
        </div>
        <div className="space-y-4">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <Skeleton className="h-10 w-16" animation={animation} />
                  <div>
                    <Skeleton className="h-5 w-32 mb-1" animation={animation} />
                    <Skeleton className="h-4 w-24" animation={animation} />
                  </div>
                </div>
                <Skeleton className="h-8 w-24" variant="rectangular" animation={animation} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <Skeleton className="h-6 w-32 mb-4" animation={animation} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="p-4 bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700">
              <Skeleton
                variant="rectangular"
                width={24}
                height={24}
                className="mb-2"
                animation={animation}
              />
              <Skeleton className="h-5 w-28 mb-1" animation={animation} />
              <Skeleton className="h-4 w-32" animation={animation} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

DashboardSkeleton.displayName = 'DashboardSkeleton';
AdminDashboardSkeleton.displayName = 'AdminDashboardSkeleton';
TherapistDashboardSkeleton.displayName = 'TherapistDashboardSkeleton';
